import { CreditCard, AlertTriangle, ExternalLink, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminBillingDiagnostics,
  type PssByStatus,
  type PastDueSubscription,
  type CanceledSubscription,
  type PendingMissingStripe,
  type StripeConnectIncomplete,
  type OrgPastDue,
  type ConnectBillingEvent,
  type BillingEvent,
} from '@/hooks/admin/useAdminBillingDiagnostics';

function PssStatusBreakdown({ data }: { data: PssByStatus }) {
  const entries = Object.entries(data).sort(([a], [b]) => {
    const order = ['active', 'trialing', 'pending', 'past_due', 'canceled'];
    return order.indexOf(a) - order.indexOf(b);
  });
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    trialing: 'outline',
    pending: 'secondary',
    past_due: 'destructive',
    canceled: 'destructive',
  };
  return (
    <Card className="border-wt-border bg-wt-bg-secondary">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-xs text-wt-text-muted font-normal">Subscriptions by Status</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="flex flex-wrap gap-2">
          {entries.map(([status, count]) => (
            <Badge key={status} variant={variants[status] ?? 'secondary'} className="gap-1 text-xs">
              {status}: {count}
            </Badge>
          ))}
          {entries.length === 0 && <p className="text-xs text-wt-text-muted">No subscriptions</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function SimpleTable<T extends Record<string, unknown>>({ rows, columns, emptyText }: {
  rows: T[];
  columns: { key: string; label: string; render?: (row: T) => React.ReactNode }[];
  emptyText?: string;
}) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">{emptyText ?? 'None'}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          {columns.map(c => (
            <th key={c.key} className="text-left py-1.5 pr-3 font-medium">{c.label}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={(row.id as string) ?? i} className="border-b border-wt-border/50">
              {columns.map(c => (
                <td key={c.key} className="py-1.5 pr-3">
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCents(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export function BillingDiagnosticsTab() {
  const { data: d, isLoading, error } = useAdminBillingDiagnostics();

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {d && (
        <div className="space-y-4">
          {/* Health summary */}
          {d.past_due_subscriptions.length === 0 && d.orgs_past_due.length === 0 && d.pending_missing_stripe.length === 0 && d.stripe_connect_incomplete.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-green-500 py-1">
              <CheckCircle2 className="h-4 w-4" />
              All billing systems healthy — no issues detected.
            </div>
          )}

          <PssStatusBreakdown data={d.pss_by_status} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Past Due Subscriptions */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-red-primary" />
                  Past Due Subscriptions ({d.past_due_subscriptions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<PastDueSubscription>
                  rows={d.past_due_subscriptions}
                  emptyText="No past-due subscriptions"
                  columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'plan_name', label: 'Plan' },
                    { key: 'price_cents', label: 'Price', render: r => <span className="font-mono">{formatCents(r.price_cents)}</span> },
                    { key: 'current_period_end', label: 'Period End', render: r => formatDate(r.current_period_end) },
                  ]}
                />
                {d.past_due_subscriptions.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Check Stripe invoice status and contact org owner.</p>
                )}
              </CardContent>
            </Card>

            {/* Pending Missing Stripe */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-wt-amber-primary" />
                  Pending — No Stripe ID ({d.pending_missing_stripe.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<PendingMissingStripe>
                  rows={d.pending_missing_stripe}
                  emptyText="All pending subscriptions have Stripe IDs"
                  columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'plan_name', label: 'Plan' },
                    { key: 'created_at', label: 'Created', render: r => formatDate(r.created_at) },
                  ]}
                />
                {d.pending_missing_stripe.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Checkout session may not have completed. Inspect billing setup.</p>
                )}
              </CardContent>
            </Card>

            {/* Stripe Connect Incomplete */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-wt-amber-primary" />
                  Stripe Connect Incomplete ({d.stripe_connect_incomplete.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<StripeConnectIncomplete>
                  rows={d.stripe_connect_incomplete}
                  emptyText="All Connect accounts fully onboarded"
                  columns={[
                    { key: 'name', label: 'Org' },
                    { key: 'charges_enabled', label: 'Charges', render: r => (
                      <Badge variant={r.charges_enabled ? 'default' : 'destructive'} className="text-[10px]">
                        {r.charges_enabled ? 'Yes' : 'No'}
                      </Badge>
                    )},
                    { key: 'payouts_enabled', label: 'Payouts', render: r => (
                      <Badge variant={r.payouts_enabled ? 'default' : 'destructive'} className="text-[10px]">
                        {r.payouts_enabled ? 'Yes' : 'No'}
                      </Badge>
                    )},
                    { key: 'stripe_connect_account_id', label: 'Stripe', render: r => (
                      <a
                        href={`https://dashboard.stripe.com/connect/accounts/${r.stripe_connect_account_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-400 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> Open
                      </a>
                    )},
                  ]}
                />
              </CardContent>
            </Card>

            {/* Orgs Past Due */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-red-primary" />
                  Orgs Past Due ({d.orgs_past_due.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<OrgPastDue>
                  rows={d.orgs_past_due}
                  emptyText="No orgs past due"
                  columns={[
                    { key: 'name', label: 'Org' },
                    { key: 'billing_email', label: 'Billing Email' },
                    { key: 'last_payment_failed_at', label: 'Failed At', render: r => formatDate(r.last_payment_failed_at) },
                    { key: 'grace_period_ends_at', label: 'Grace Ends', render: r => {
                      if (!r.grace_period_ends_at) return '—';
                      const graceEnd = new Date(r.grace_period_ends_at);
                      const expired = graceEnd < new Date();
                      return (
                        <span className={expired ? 'text-wt-red-primary font-medium' : ''}>
                          {formatDate(r.grace_period_ends_at)}{expired ? ' (expired)' : ''}
                        </span>
                      );
                    }},
                  ]}
                />
                {d.orgs_past_due.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Contact billing email. Orgs past grace period may lose access.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Canceled */}
          {d.recent_canceled_subscriptions.length > 0 && (
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium">Recently Canceled ({d.recent_canceled_subscriptions.length})</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<CanceledSubscription>
                  rows={d.recent_canceled_subscriptions}
                  columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'plan_name', label: 'Plan' },
                    { key: 'price_cents', label: 'Price', render: r => <span className="font-mono">{formatCents(r.price_cents)}</span> },
                    { key: 'updated_at', label: 'Canceled', render: r => formatDate(r.updated_at) },
                  ]}
                />
              </CardContent>
            </Card>
          )}

          {/* Recent Connect Billing Events */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium">Recent Connect Events (30d)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <SimpleTable<ConnectBillingEvent>
                rows={d.recent_connect_events}
                emptyText="None in last 30 days"
                columns={[
                  { key: 'event_type', label: 'Event', render: r => <span className="font-mono">{r.event_type}</span> },
                  { key: 'org_name', label: 'Org' },
                  { key: 'property_name', label: 'Property' },
                  { key: 'total_amount_cents', label: 'Amount', render: r => <span className="font-mono">{formatCents(r.total_amount_cents)}</span> },
                  { key: 'platform_fee_cents', label: 'Fee', render: r => <span className="font-mono">{formatCents(r.platform_fee_cents)}</span> },
                  { key: 'status', label: 'Status', render: r => r.status ? <Badge variant="secondary" className="text-[10px]">{r.status}</Badge> : <>—</> },
                  { key: 'created_at', label: 'When', render: r => formatDateTime(r.created_at) },
                ]}
              />
            </CardContent>
          </Card>

          {/* Recent Billing Events */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium">Recent Billing Events (30d)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <SimpleTable<BillingEvent>
                rows={d.recent_billing_events}
                emptyText="None in last 30 days"
                columns={[
                  { key: 'event_type', label: 'Event', render: r => <span className="font-mono">{r.event_type}</span> },
                  { key: 'org_name', label: 'Org' },
                  { key: 'status', label: 'Status', render: r => (
                    <Badge variant={r.status === 'succeeded' ? 'default' : r.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                      {r.status}
                    </Badge>
                  )},
                  { key: 'created_at', label: 'When', render: r => formatDateTime(r.created_at) },
                ]}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </AdminTabShell>
  );
}
